import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { HorarioEfectivo } from '../../../api/ops/reservas';

type DiaEfectivo = HorarioEfectivo['days'][number];

/**
 * El horario del día (G245): el calendario del negocio (`listCalendars` con `kind=business`) y sus tramos de ese día
 * (`getEffectiveHours`), para «Hoy abre de 13:00 a 16:30 y de 20:00 a 00:00.». Sin permiso para ver horarios o si falla,
 * null (la línea queda sin texto).
 */
export interface HorarioDelDia {
    dia: DiaEfectivo;
    texto: (quien: string) => string;
}

export function textoHorario(d: DiaEfectivo, quien: string): string {
    if (d.status === 'closed' || d.status === 'holiday' || d.intervals.length === 0) {
        return d.status === 'closed' && (d.source === 'own_schedule' || d.source === 'inherited') ? `${quien} no abre (cierre semanal).` : `${quien} no abre.`;
    }
    const tramos = d.intervals.map((t) => `de ${t.start_local} a ${t.end_local}`);
    const unidos = tramos.length === 1 ? tramos[0] ?? '' : `${tramos.slice(0, -1).join(', ')} y ${tramos[tramos.length - 1] ?? ''}`;
    return `${quien} abre ${unidos}.`;
}

export function useHorarioDelDia(ops: Operaciones, negocio: string, dia: string, puede: boolean): HorarioDelDia | null {
    const [h, setH] = useState<{ dia: string; d: DiaEfectivo } | null>(null);
    useEffect(() => {
        if (!puede) {
            return;
        }
        let vivo = true;
        ops.listCalendars(negocio, { kind: 'business', limit: 1 })
            .then((l) => {
                const c = l.data[0];
                return c === undefined ? null : ops.getEffectiveHours(negocio, c.id, dia, dia);
            })
            .then((e) => {
                const d = e?.days.find((x) => x.local_date === dia);
                if (vivo && d !== undefined) {
                    setH({ dia, d });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, dia, puede]);
    if (h === null || h.dia !== dia) {
        return null;
    }
    const d = h.d;
    return { dia: d, texto: (quien) => textoHorario(d, quien) };
}
